<template>
    <div class="space-y-6">
        <h2 class="text-heading-md">Card Button Examples</h2>

        <div class="grid grid-cols-2 gap-8">
            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Basic Card Buttons</h3>
                <div>
                    <DsCardButton @click="handleCardButtonClick('View Details')">
                        <div>
                            <h4 class="text-content-lg font-semibold">View Details</h4>
                            <p class="text-content-sm text-content-secondary">Click to see more information about this item</p>
                        </div>
                    </DsCardButton>

                    <DsCardButton @click="handleCardButtonClick('Navigate to Settings')">
                        <div>
                            <h4 class="text-content-lg font-semibold">Account Settings</h4>
                            <p class="text-content-sm text-content-secondary">Manage your account preferences and security</p>
                        </div>
                    </DsCardButton>

                    <DsCardButton @click="handleCardButtonClick('Open Documentation')">
                        <div>
                            <h4 class="text-content-lg font-semibold">Documentation</h4>
                            <p class="text-content-sm text-content-secondary">Learn how to use all features</p>
                        </div>
                    </DsCardButton>
                </div>
                <div class="mt-3 text-content-sm text-content-secondary">Last clicked: {{ lastClickedButton || 'None' }}</div>
            </div>

            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Card Button with Rich Content</h3>
                <DsCardButton ariaLabel="View project dashboard">
                    <div class="flex items-center gap-4">
                        <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-ds-primary-100">
                            <span class="text-2xl">📊</span>
                        </div>
                        <div class="flex-1">
                            <h4 class="text-content-lg font-semibold">Project Dashboard</h4>
                            <p class="text-content-sm text-content-secondary">View analytics, progress, and team activity</p>
                        </div>
                    </div>
                </DsCardButton>

                <div class="mt-4">
                    <DsCardButton ariaLabel="View team members">
                        <div class="flex items-center gap-4">
                            <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-ds-green-100">
                                <span class="text-2xl">👥</span>
                            </div>
                            <div class="flex-1">
                                <h4 class="text-content-lg font-semibold">Team Members</h4>
                                <p class="text-content-sm text-content-secondary">Manage team access and permissions</p>
                            </div>
                        </div>
                    </DsCardButton>
                </div>
            </div>
        </div>

        <div class="grid grid-cols-2 gap-8">
            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Card Buttons in a List</h3>
                <small>(uses DsCardOptionList, same space as checkbox/radio lists)</small>
                <DsCardOptionList spacing="spaced">
                    <DsCardButton
                        v-for="action in actionItems"
                        :key="action.id"
                        @click="handleActionClick(action)"
                    >
                        <div>
                            <h4 class="text-content-lg font-semibold">{{ action.title }}</h4>
                            <p class="text-content-sm text-content-secondary">
                                {{ action.description }}
                            </p>
                        </div>
                    </DsCardButton>
                </DsCardOptionList>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsCardOptionList from '@/components/design-system/form/input/DsCardOptionList.vue';
import DsCardButton from '@ds/form/input/DsCardButton.vue';
import { ref } from 'vue';

// Card button states
const lastClickedButton = ref('');

// Action items for card button list
const actionItems = ref([
    {
        id: 1,
        title: 'Create New Project',
        description: 'Start a new project from scratch',
    },
    {
        id: 2,
        title: 'Import Data',
        description: 'Import existing data from CSV or Excel',
    },
    {
        id: 3,
        title: 'Generate Report',
        description: 'Create a comprehensive analytics report',
    },
    {
        id: 4,
        title: 'Export Results',
        description: 'Download your data in various formats',
    },
]);

// Methods
const handleCardButtonClick = (action: string) => {
    lastClickedButton.value = action;
};

const handleActionClick = (action: { id: number; title: string; description: string }) => {
    lastClickedButton.value = action.title;
};
</script>
